import { useCallback, useEffect, useRef, useState } from 'react';

// Voice messages in the browser: MediaRecorder for the audio, an AnalyserNode
// for the live level meter and the waveform the bubble draws. Chrome records
// WebM/Opus and Safari MP4/AAC; the server stores an MP3 that plays anywhere.

export const MAX_VOICE_SECONDS = 300;
const WAVEFORM_BARS = 48;

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return (
    ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'].find((t) =>
      MediaRecorder.isTypeSupported(t)
    ) || ''
  );
}

/** Fold level samples into `bars` peaks, scaled so the loudest is 1. */
export function toWaveform(levels, bars = WAVEFORM_BARS) {
  if (!levels.length) return [];
  const step = levels.length / bars;
  const out = [];
  for (let i = 0; i < bars; i++) {
    const from = Math.floor(i * step);
    const to = Math.max(Math.floor((i + 1) * step), from + 1);
    out.push(Math.max(...levels.slice(from, to)));
  }
  const peak = Math.max(...out, 0.01);
  return out.map((v) => Math.round((v / peak) * 100) / 100);
}

export const voiceSupported = () =>
  typeof window !== 'undefined' &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
  typeof MediaRecorder !== 'undefined';

export default function useVoiceRecorder({ onMaxReached } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [meter, setMeter] = useState([]); // the last ~36 levels, for the live bars

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);
  const levelsRef = useRef([]);
  const startedAtRef = useRef(0);
  const onMaxRef = useRef(onMaxReached);
  onMaxRef.current = onMaxReached;

  const release = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    audioCtxRef.current = null;
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    levelsRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    recorder.start(250);
    recorderRef.current = recorder;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);

    startedAtRef.current = Date.now();
    setSeconds(0);
    setMeter([]);
    setRecording(true);
    timerRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const x = (buf[i] - 128) / 128;
        sum += x * x;
      }
      const level = Math.min(1, Math.sqrt(sum / buf.length) * 4);
      levelsRef.current.push(level);
      setMeter(levelsRef.current.slice(-36));
      const secs = (Date.now() - startedAtRef.current) / 1000;
      setSeconds(secs);
      if (secs >= MAX_VOICE_SECONDS && onMaxRef.current) onMaxRef.current();
    }, 100);
  }, []);

  /** Stop and hand back { file, durationSec, waveform, localUrl }. */
  const stop = useCallback(
    () =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder) return resolve(null);
        const durationSec = Math.round(((Date.now() - startedAtRef.current) / 1000) * 10) / 10;
        recorder.onstop = () => {
          const type = recorder.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
          const file = new File([blob], `voice-message.${ext}`, { type });
          const waveform = toWaveform(levelsRef.current);
          release();
          resolve({ file, durationSec, waveform, localUrl: URL.createObjectURL(blob) });
        };
        recorder.stop();
      }),
    [release]
  );

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch (_) {
        /* already stopped */
      }
    }
    release();
  }, [release]);

  useEffect(() => () => cancel(), [cancel]);

  return { recording, seconds, meter, start, stop, cancel };
}
